import type { PromptSnippetInput } from "@/schema/prompt-snippet"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  createPromptSnippet,
  createPromptSnippetOverride,
  destroyPromptSnippet,
  destroyPromptSnippetOverride,
  updatePromptSnippet,
} from "./api"

export function useCreatePromptSnippet() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ promptSnippet, scope }: { promptSnippet: PromptSnippetInput; scope?: "company" | "book" }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createPromptSnippet(context, promptSnippet, scope ? { scope } : undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompt-snippets"] })
    },
  })
}

export function useUpdatePromptSnippet() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, promptSnippet }: { id: string; promptSnippet: Partial<PromptSnippetInput> }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updatePromptSnippet(context, id, promptSnippet)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompt-snippets"] })
    },
  })
}

export function useDestroyPromptSnippet() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return destroyPromptSnippet(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompt-snippets"] })
    },
  })
}

export function useCreatePromptSnippetOverride() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      promptSnippet: PromptSnippetInput & { system_key: string; hidden?: boolean; scope?: "company" | "book" }
    ) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createPromptSnippetOverride(context, promptSnippet)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompt-snippets"] })
    },
  })
}

export function useDestroyPromptSnippetOverride() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (target: string | { systemKey: string; id: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return typeof target === "string"
        ? destroyPromptSnippetOverride(context, target)
        : destroyPromptSnippetOverride(context, target.systemKey, target.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompt-snippets"] })
    },
  })
}
